/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Modal from 'src/components/Modal';
import { Input, TextArea } from 'src/components/Input';
import Button from 'src/components/Button';
import { AntdForm } from 'src/components';
import { SelectValue } from 'antd/lib/select';
import rison from 'rison';
import { t, SupersetClient, styled } from '@superset-ui/core';
import Chart, { Slice } from 'src/types/Chart';
import { getClientErrorObject } from 'src/utils/getClientErrorObject';
import withToasts from 'src/components/MessageToasts/withToasts';
import { Form } from 'src/components/Form';
import AsyncSelect from 'src/components/AsyncSelect';

export type PropertiesModalProps = {
  slice: Slice;
  show: boolean;
  onHide: () => void;
  onSave: (chart: Chart) => void;
  permissionsError?: string;
  existingOwners?: SelectValue;
  addSuccessToast: (msg: string) => void;
};

const FormItem = AntdForm.Item;

const StyledFormItem = styled(AntdForm.Item)`
  margin-bottom: 0;
  .ant-form-item {
    margin-bottom: 0px !important;
}
`;


const StyledHeaderText = styled.div`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  /* identical to box height, or 125% */

  display: flex;
  align-items: center;
  margin-bottom: 10px;
  color: #000000;
`;
const StyledFormLabel = styled.div`
.ant-form-item-label{
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  color: #272833;}
`;
function PropertiesModal({
  slice,
  onHide,
  onSave,
  show,
  addSuccessToast,
}: PropertiesModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [form] = AntdForm.useForm();
  // values of form inputs
  const [name, setName] = useState(slice.slice_name || '');
  const [selectedOwners, setSelectedOwners] = useState<SelectValue | null>(null);

  const [description, setDescription] = useState(slice.description || '');
  const [cacheTimeout, setCacheTimeout] = useState(
    slice.cache_timeout != null ? slice.cache_timeout : '',
  );
  // const [owners, setOwners] = useState<OptionsType<any> | null>(null);


  function showError({ error, statusText, message }: any) {
    let errorText = error || statusText || t('An error has occurred');
    if (message === 'Forbidden') {
      errorText = t('You do not have permission to edit this chart');
    }
    Modal.error({
      title: 'Error',
      content: errorText,
      okButtonProps: { danger: true, className: 'btn-danger' },
    });
  }

  const fetchChartOwners = useCallback(
    async function fetchChartOwners() {
      try {
        // await SupersetClient.get({
        //   endpoint: `/api/v1/chart/${slice.slice_id}`,
        // });
        const response = await SupersetClient.get({
          endpoint: `/api/v1/chart/${slice.slice_id}`,
        });
        const chart = response.json.result;
        setSelectedOwners(
          chart?.owners?.map((owner: any) => ({
            value: owner.id,
            label: `${owner.first_name} ${owner.last_name}`,
          })),
        );
      } catch (response) {
        const clientError = await getClientErrorObject(response);
        showError(clientError);
      }
    },
    [slice.slice_id],
  );

  const loadOptions = useMemo(
    () =>
      (input = '', page: number, pageSize: number) => {
        const query = rison.encode({
          filter: input,
          page,
          page_size: pageSize,
        });
        return SupersetClient.get({
          endpoint: `/api/v1/chart/related/owners?q=${query}`,
        }).then(response => ({
          data: response.json.result.map(
            (item: { value: number; text: string }) => ({
              value: item.value,
              label: item.text,
            }),
          ),
          totalCount: response.json.count,
        }));
      },
    [],
  );

  const onSubmit = async (values: {
    certified_by?: string;
    certification_details?: string;
    description?: string;
    cache_timeout?: number;
  }) => {
    setSubmitting(true);
    const {
      certified_by: certifiedBy,
      certification_details: certificationDetails,
      description,
      cache_timeout: cacheTimeout,
    } = values;
    const payload: { [key: string]: any } = {
      slice_name: name || null,
      description: description || null,
      cache_timeout: cacheTimeout || null,
      certified_by: certifiedBy || null,
      certification_details:
        certifiedBy && certificationDetails ? certificationDetails : null,
    };
    if (selectedOwners) {
      payload.owners = (
        selectedOwners as {
          value: number;
          label: string;
        }[]
      ).map(o => o.value);
    }
    try {
      const res = await SupersetClient.put({
        endpoint: `/api/v1/chart/${slice.slice_id}`,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // update the redux state
      const updatedChart = {
        ...payload,
        ...res.json.result,
        id: slice.slice_id,
      };
      onSave(updatedChart);
      addSuccessToast(t('Chart properties updated'));
      onHide();
    } catch (res) {
      const clientError = await getClientErrorObject(res);
      showError(clientError);
    }
    setSubmitting(false);
  };

  // const ownersLabel = t('Owners');

  // get the owners of this slice
  useEffect(() => {
    fetchChartOwners();
  }, [fetchChartOwners]);

  // update name after it's changed in another modal
  useEffect(() => {
    setName(slice.slice_name || '');
  }, [slice.slice_name]);

  const query = rison.encode({
    filter: '',
    page: 0,
    page_size: 200,
  });
  const dataEndpoint = `/api/v1/chart/related/owners?q=${query}`;
  const mutator = (json: any) => {
    return json.result?.map((opt: {text: string, value: number}) => ({
      value: opt.value,
      label: opt.text
    }));
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      title="Edit Chart Properties"
      footer={
        <>
          <Button
            data-test="properties-modal-cancel-button"
            htmlType="button"
            buttonSize="small"
            onClick={onHide}
            buttonStyle="tertiary"
            cta
          >
            {t('Cancel')}
          </Button>
          <Button
            data-test="properties-modal-save-button"
            htmlType="submit"
            buttonSize="small"
            buttonStyle="primary"
            onClick={form.submit}
            disabled={submitting || !name || slice.is_managed_externally}
            tooltip={
              slice.is_managed_externally
                ? t(
                    "This chart is managed externally, and can't be edited in Superset",
                  )
                : ''
            }
            cta
          >
            {t('Save')}
          </Button>
        </>
      }
      responsive
      wrapProps={{ 'data-test': 'properties-edit-modal' }}
    >
      <Form form={form} onFinish={onSubmit} layout="vertical">
        <div style={{backgroundColor: '#F9FAFE',padding: '10px'}} className="edit-chart-form">
          <div style={{display:'flex',justifyContent:'space-between',width:'100%'}}>
              <div style={{width:'48%'}}>
                <StyledHeaderText>Basic information</StyledHeaderText>
                <StyledFormLabel><FormItem label={t('Name')} required>
                  <Input
                    aria-label={t('Name')}
                    name="name"
                    data-test="properties-modal-name-input"
                    type="text"
                    value={name}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setName(event.target.value ?? '')
                    }
                  />
                </FormItem></StyledFormLabel>
                <FormItem label={t('Description')}>
                  <TextArea
                    rows={1}
                    name="description"
                    value={description}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setDescription(event.target.value ?? '')
                    }
                    style={{ maxWidth: '100%' }}
                  />
                  <p className="help-block" style={{fontSize:'11px',fontWeight:'normal',lineHeight:'14px',color:'rgba(146,154,168,0.6)'}}>
                    {t(
                      'The description can be displayed as widget headers in the dashboard view. Supports markdown.',
                    )}
                  </p>
                </FormItem>
              </div>
              <div style={{width:'48%'}}>
                <StyledHeaderText>{t('Certification')}</StyledHeaderText>
                <FormItem>
                  <StyledFormItem label={t('Certified by')} name="certified_by">
                    <Input aria-label={t('Certified by')} />
                  </StyledFormItem>
                  <p className="help-block" style={{fontSize:'11px',fontWeight:'normal',lineHeight:'14px',color:'rgba(146,154,168,0.6)',
    marginTop: '-14px'}}>
                    {t('Person or group that has certified this chart.')}
                  </p>
                </FormItem>
                <FormItem>
                  <StyledFormItem
                    label={t('Certification details')}
                    name="certification_details"
                  >
                    <Input aria-label={t('Certification details')} />
                  </StyledFormItem>
                  <p className="help-block" style={{fontSize:'11px',fontWeight:'normal',lineHeight:'14px',color:'rgba(146,154,168,0.6)',marginTop: '-14px'}}>
                    {t(
                        'Any additional detail to show in the certification tooltip.',
                    )}
                  </p>
                </FormItem>
              </div>
            
          </div>
          <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',width:'100%'}}>
            <div style={{width:'48%'}}>
              <StyledHeaderText>{t('Configuration')}</StyledHeaderText>
              <FormItem label={t('Cache timeout')}>
                <Input
                  name="cacheTimeout"
                  type="text"
                  value={cacheTimeout}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const targetValue = event.target.value ?? '';
                    setCacheTimeout(targetValue.replace(/[^0-9]/, ''));
                  }}
                />
                <p className="help-block" style={{fontSize:'11px',fontWeight:'normal',lineHeight:'14px',color:'rgba(146,154,168,0.6)'}}>
                  {t(
                    "Duration (in seconds) of the caching timeout for this chart. Note this defaults to the dataset's timeout if undefined.",
                  )}
                </p>
              </FormItem>
            </div>
            <div style={{width:'48%'}}>
              <StyledHeaderText>{t('Access')}</StyledHeaderText>
              <FormItem label={t('Owners')}>
                <AsyncSelect
                  isMulti
                  name="owners"
                  value={selectedOwners || []}
                  loadOptions={loadOptions}
                  defaultOptions // load options on render
                  cacheOptions
                  onChange={setSelectedOwners}
                  // disabled={!owners}
                  filterOption={null} // options are filtered at the api
                  dataEndpoint={dataEndpoint}
                  mutator={mutator}
                />
                <p className="help-block" style={{fontSize:'11px',fontWeight:'normal',lineHeight:'14px',color:'rgba(146,154,168,0.6)'}}>
                  {t(
                    'A list of users who can alter the chart. Searchable by name or username.',
                  )}
                </p>
              </FormItem>    
            </div>
          </div>
        </div>
      </Form>
    </Modal>
  );
}

export default withToasts(PropertiesModal);
